import { useEffect, useRef } from 'react';

// Intervalo por defecto del poller de waitlist (D4/R3). Configurable por
// parámetro; se mantiene el valor histórico (5 s) del `setInterval` previo.
export const WAITLIST_POLL_INTERVAL_MS = 5000;

// Poller sin solapamiento (D4/R3). Corrige L1 (13 requests simultáneas por
// overlap de `setInterval`). Dos mecanismos combinados:
//
//  - **Self-scheduling**: cada tick reagenda el siguiente vía `setTimeout` a
//    cadencia fija (no un `setInterval` global) — sin drift acumulado.
//  - **inFlightRef**: si un tick se dispara mientras hay una request viva, se
//    **descarta** (se mantiene la en curso), garantizando **≤1 en vuelo**
//    (R3.AC1-AC3: "descartar el disparo nuevo manteniendo 1 activo"). Al
//    descartar emite la señal R7 `console.debug('[poller] tick skipped
//    (in-flight)')` (D8).
//
// Reagendar a cadencia fija (en vez de solo en el `finally`) es lo que hace que
// el guard `inFlightRef` sea alcanzable bajo latencia > intervalo: es
// precisamente ahí donde el disparo solapado debe descartarse y emitir la señal.
//
// `fn` puede ser async; sus errores se tragan para no matar el poller.
//
// `options.immediate` (default `true`) controla el primer disparo: con el
// valor por defecto se mantiene el comportamiento histórico (tick inmediato
// al montar, igual que el `setInterval` previo). Con `immediate:false` el
// primer disparo se reagenda vía `schedule()` (espera `intervalMs` antes del
// primer `tick()`) — usado por el auto-refresh del Day Planner para no
// duplicar el fetch de montaje.
export function usePoller(fn, intervalMs = WAITLIST_POLL_INTERVAL_MS, enabled = true, options = {}) {
  const { immediate = true } = options;
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    let timer = null;
    let inFlight = false;

    const schedule = () => {
      if (stopped) return;
      timer = setTimeout(tick, intervalMs);
    };

    const tick = async () => {
      if (stopped) return;
      // Reagenda el siguiente intento a cadencia fija (self-scheduling).
      schedule();
      if (inFlight) {
        // Disparo solapado: hay una request viva → descartar (≤1 en vuelo).
        console.debug('[poller] tick skipped (in-flight)');
        return;
      }
      inFlight = true;
      try {
        await fnRef.current();
      } catch {
        // Un fallo puntual no debe detener el poller.
      } finally {
        inFlight = false;
      }
    };

    if (immediate) {
      // Primer disparo inmediato (equivalente al fetch previo al `setInterval`).
      tick();
    } else {
      // Primer disparo diferido: se reagenda a `intervalMs`, sin tick a t=0.
      schedule();
    }

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [intervalMs, enabled, immediate]);
}
